import { Copy, Link2, PackagePlus, RotateCcw, Rocket, Trash2, Upload, XCircle } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { AppRecord, ReleaseArtifactRecord, ReleaseChannel, ReleaseDownloadGrantRecord, ReleaseRecord, ReleaseStatus } from "@/api/types";
import {
  useCreateReleaseDownloadGrantMutation,
  useCreateReleaseMutation,
  useDeleteReleaseArtifactMutation,
  useDeleteReleaseMutation,
  usePublishReleaseMutation,
  useReleaseArtifactsQuery,
  useReleaseDownloadGrantsQuery,
  useReleasesQuery,
  useRevokeReleaseDownloadGrantMutation,
  useRestoreReleaseMutation,
  useUploadReleaseArtifactMutation,
  useYankReleaseMutation
} from "@/api/query-hooks";
import { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { isMailArchiverApp, MailArchiverIcon } from "@/components/mailarchiver-icon";
import { formatDateTime } from "@/lib/date";

const channels: ReleaseChannel[] = ["STABLE", "BETA", "ALPHA"];
const releaseOperatingSystems = ["linux", "windows", "darwin"] as const;
const releaseArchitectures = ["amd64", "arm64"] as const;
const artifactKinds = ["archive", "installer"] as const;
const artifactSigningStates = ["not_applicable", "unsigned", "signed", "notarized", "stapled"] as const;
const webView2Strategies = ["download", "embed", "browser", "error"] as const;

export function ReleasesPanel({ apps }: { apps: AppRecord[] }) {
  const { t, i18n } = useTranslation(["apps", "common"]);
  const [appId, setAppId] = useState(apps[0]?.id ?? "");
  const params = useMemo(() => {
    const value = new URLSearchParams();
    if (appId) {
      value.set("appId", appId);
    }
    value.set("limit", "50");
    return value;
  }, [appId]);
  const releasesQuery = useReleasesQuery(params);

  if (apps.length === 0) {
    return null;
  }

  const releases = releasesQuery.data?.items ?? [];

  return (
    <section className="grid gap-4 rounded-md border bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{t("releases")}</h3>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("releasesDescription")}</p>
        </div>
        <Select value={appId} onValueChange={setAppId}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {apps.map((app) => (
              <SelectItem key={app.id} value={app.id}>
                <span className="flex items-center gap-2">
                  {isMailArchiverApp(app.id) && <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />}
                  {app.id}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <CreateReleaseForm appId={appId} />

      <div className="overflow-hidden rounded-md border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[940px] border-collapse text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t("version")}</th>
                <th className="px-3 py-2">{t("channel")}</th>
                <th className="px-3 py-2">{t("status")}</th>
                <th className="px-3 py-2">{t("titleField")}</th>
                <th className="px-3 py-2">{t("publishedAt")}</th>
                <th className="px-3 py-2">{t("artifacts")}</th>
                <th className="px-3 py-2 text-right">{t("common:actions.open")}</th>
              </tr>
            </thead>
            <tbody>
              {releasesQuery.isLoading ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>
                    {t("common:states.loading")}
                  </td>
                </tr>
              ) : releases.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={7}>
                    {t("noReleases")}
                  </td>
                </tr>
              ) : (
                releases.map((release) => <ReleaseRow key={release.id} release={release} locale={i18n.language} />)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CreateReleaseForm({ appId }: { appId: string }) {
  const { t } = useTranslation(["apps", "common"]);
  const mutation = useCreateReleaseMutation();
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState<ReleaseChannel>("STABLE");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <form
      className="grid gap-3 border-t pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(
          { appId, version: version.trim(), channel, title: title.trim(), notes: notes.trim() },
          {
            onSuccess: () => {
              setVersion("");
              setTitle("");
              setNotes("");
              toast.success(t("releaseCreated"));
            },
            onError: showMutationError
          }
        );
      }}
    >
      <div className="grid gap-3 lg:grid-cols-[minmax(9rem,12rem)_9rem_minmax(12rem,1fr)_auto]">
        <Field label={t("version")}>
          <Input value={version} onChange={(event) => setVersion(event.target.value)} placeholder="1.2.3" required />
        </Field>
        <Field label={t("channel")}>
          <Select value={channel} onValueChange={(value) => setChannel(value as ReleaseChannel)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {channels.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("titleField")}>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <div className="flex items-end">
          <Button type="submit" disabled={mutation.isPending || !version.trim()}>
            <PackagePlus className="h-4 w-4" />
            {t("createRelease")}
          </Button>
        </div>
      </div>
      <Field label={t("notes")}>
        <Textarea className="min-h-20" value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>
    </form>
  );
}

function ReleaseRow({ release, locale }: { release: ReleaseRecord; locale: string }) {
  const { t } = useTranslation(["apps", "common"]);
  const artifactsQuery = useReleaseArtifactsQuery(release.id);
  const publishMutation = usePublishReleaseMutation();
  const deleteMutation = useDeleteReleaseMutation();
  const artifacts = artifactsQuery.data?.items ?? [];

  return (
    <>
      <tr className="border-t align-top">
        <td className="px-3 py-2 font-mono text-xs">{release.version}</td>
        <td className="px-3 py-2">{release.channel}</td>
        <td className="px-3 py-2">
          <StatusBadge status={release.status} />
        </td>
        <td className="px-3 py-2">{release.title || "—"}</td>
        <td className="px-3 py-2 text-muted-foreground">{release.publishedAt ? formatDateTime(release.publishedAt, locale) : "—"}</td>
        <td className="px-3 py-2">{artifacts.length}</td>
        <td className="px-3 py-2">
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={release.status !== "DRAFT" || publishMutation.isPending}
              onClick={() => publishMutation.mutate(release.id, { onSuccess: () => toast.success(t("releasePublished")), onError: showMutationError })}
            >
              <Rocket className="h-4 w-4" />
              {t("publish")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={release.status !== "DRAFT" || deleteMutation.isPending}
              onClick={() => {
                if (!window.confirm(t("deleteReleaseConfirm", { version: release.version }))) {
                  return;
                }
                deleteMutation.mutate(release.id, { onSuccess: () => toast.success(t("releaseDeleted")), onError: showMutationError });
              }}
            >
              <Trash2 className="h-4 w-4" />
              {t("deleteRelease")}
            </Button>
            {release.status === "YANKED" ? (
              <RestoreReleaseDialog release={release} />
            ) : (
              <YankReleaseDialog release={release} disabled={release.status !== "PUBLISHED"} />
            )}
          </div>
        </td>
      </tr>
      <tr className="border-t bg-muted/35">
        <td className="px-3 py-3" colSpan={7}>
          <ReleaseArtifacts release={release} artifacts={artifacts} loading={artifactsQuery.isLoading} />
        </td>
      </tr>
    </>
  );
}

function YankReleaseDialog({ release, disabled }: { release: ReleaseRecord; disabled: boolean }) {
  const { t } = useTranslation(["apps", "common"]);
  const mutation = useYankReleaseMutation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setReason("");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled || mutation.isPending}>
          <XCircle className="h-4 w-4" />
          {t("yank")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("yankReleaseTitle", { version: release.version })}</DialogTitle>
          <DialogDescription>{t("yankReleaseDescription")}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const normalizedReason = reason.trim();
            if (!normalizedReason) {
              return;
            }
            mutation.mutate(
              { releaseId: release.id, reason: normalizedReason },
              {
                onSuccess: () => {
                  setOpen(false);
                  toast.success(t("releaseYanked"));
                },
                onError: showMutationError
              }
            );
          }}
        >
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{t("yankReleaseImpact")}</div>
          <Field label={t("yankReasonPrompt")}>
            <Textarea className="min-h-24" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus required />
          </Field>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t("common:actions.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" variant="destructive" disabled={mutation.isPending || !reason.trim()}>
              <XCircle className="h-4 w-4" />
              {t("confirmYank")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RestoreReleaseDialog({ release }: { release: ReleaseRecord }) {
  const { t } = useTranslation(["apps", "common"]);
  const mutation = useRestoreReleaseMutation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setReason("");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={mutation.isPending}>
          <RotateCcw className="h-4 w-4" />
          {t("restoreRelease")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("restoreReleaseTitle", { version: release.version })}</DialogTitle>
          <DialogDescription>{t("restoreReleaseDescription")}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const normalizedReason = reason.trim();
            if (!normalizedReason) {
              return;
            }
            mutation.mutate(
              { releaseId: release.id, reason: normalizedReason },
              {
                onSuccess: () => {
                  setOpen(false);
                  toast.success(t("releaseRestored"));
                },
                onError: showMutationError
              }
            );
          }}
        >
          {release.yankedReason ? (
            <div className="rounded-md border bg-muted p-3 text-sm text-muted-foreground">
              {t("currentYankReason", { reason: release.yankedReason })}
            </div>
          ) : null}
          <Field label={t("restoreReasonPrompt")}>
            <Textarea className="min-h-24" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus required />
          </Field>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t("common:actions.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending || !reason.trim()}>
              <RotateCcw className="h-4 w-4" />
              {t("confirmRestore")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReleaseArtifacts({ release, artifacts, loading }: { release: ReleaseRecord; artifacts: ReleaseArtifactRecord[]; loading: boolean }) {
  const { t, i18n } = useTranslation(["apps", "common"]);

  return (
    <div className="grid gap-3">
      {release.status === "DRAFT" ? <UploadArtifactForm releaseId={release.id} artifacts={artifacts} /> : null}
      {loading ? (
        <div className="text-sm text-muted-foreground">{t("common:states.loading")}</div>
      ) : artifacts.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t("noArtifacts")}</div>
      ) : (
        <div className="grid gap-2">
          {artifacts.map((artifact) => (
            <ArtifactRow
              key={artifact.id}
              appId={release.appId}
              artifact={artifact}
              draft={release.status === "DRAFT"}
              published={release.status === "PUBLISHED"}
              locale={i18n.language}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactRow({
  appId,
  artifact,
  draft,
  published,
  locale
}: {
  appId: string;
  artifact: ReleaseArtifactRecord;
  draft: boolean;
  published: boolean;
  locale: string;
}) {
  const { t } = useTranslation(["apps", "common"]);
  const deleteMutation = useDeleteReleaseArtifactMutation();
  const grantParams = useMemo(() => {
    const value = new URLSearchParams();
    value.set("artifactId", artifact.id);
    value.set("limit", "10");
    return value;
  }, [artifact.id]);
  const grantsQuery = useReleaseDownloadGrantsQuery(grantParams, published);
  const grants = grantsQuery.data?.items ?? [];

  return (
    <div className="grid gap-3 rounded border bg-panel p-3">
      <div className="grid gap-2 md:grid-cols-[7rem_7rem_minmax(12rem,1fr)_8rem_auto] md:items-center">
        <div className="font-mono text-xs">{artifact.os}</div>
        <div className="font-mono text-xs">{artifact.arch}</div>
        <div className="flex min-w-0 items-center gap-2 text-sm">
          {isMailArchiverApp(appId) && <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />}
          <span className="truncate">{artifact.filename}</span>
        </div>
        <div className="text-xs text-muted-foreground">{formatBytes(artifact.sizeBytes)}</div>
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={!artifact.downloadUrl}
            onClick={() => {
              if (!artifact.downloadUrl) {
                return;
              }
              void copyText(artifact.downloadUrl, t("copiedDownloadUrl"));
            }}
          >
            <Copy className="h-4 w-4" />
            {t("copyUrl")}
          </Button>
          <CreateGrantDialog artifact={artifact} disabled={!published} />
          {draft ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!window.confirm(t("deleteArtifactConfirm", { filename: artifact.filename }))) {
                  return;
                }
                deleteMutation.mutate(
                  { releaseId: artifact.releaseId, artifactId: artifact.id },
                  { onSuccess: () => toast.success(t("artifactDeleted")), onError: showMutationError }
                );
              }}
            >
              <Trash2 className="h-4 w-4" />
              {t("deleteArtifact")}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant={artifact.kind === "installer" ? "success" : "outline"}>{t(`artifactKind.${artifact.kind}`)}</Badge>
        {artifact.defaultDownload ? <Badge variant="success">{t("defaultDownload")}</Badge> : null}
        <Badge variant="default">{t(`signingState.${artifact.signingState}`)}</Badge>
        {artifact.webview2Strategy ? <Badge variant="outline">WebView2: {artifact.webview2Strategy}</Badge> : null}
      </div>
      {published ? <ArtifactGrantList grants={grants} loading={grantsQuery.isLoading} locale={locale} /> : null}
    </div>
  );
}

function CreateGrantDialog({ artifact, disabled }: { artifact: ReleaseArtifactRecord; disabled: boolean }) {
  const { t } = useTranslation(["apps", "common"]);
  const mutation = useCreateReleaseDownloadGrantMutation();
  const [open, setOpen] = useState(false);
  const [clientID, setClientID] = useState("");
  const [ttlHours, setTTLHours] = useState("24");
  const [maxDownloads, setMaxDownloads] = useState("0");
  const [note, setNote] = useState("");
  const [issuedURL, setIssuedURL] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setIssuedURL("");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          <Link2 className="h-4 w-4" />
          {t("createGrant")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("createGrant")}</DialogTitle>
          <DialogDescription>{artifact.filename}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t("ttl")}>
              <Select value={ttlHours} onValueChange={setTTLHours}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">{t("ttl24")}</SelectItem>
                  <SelectItem value="72">{t("ttl72")}</SelectItem>
                  <SelectItem value="168">{t("ttl168")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("maxDownloads")}>
              <Input min={0} type="number" value={maxDownloads} onChange={(event) => setMaxDownloads(event.target.value)} />
            </Field>
            <Field label={t("clientId")}>
              <Input min={1} type="number" value={clientID} onChange={(event) => setClientID(event.target.value)} />
            </Field>
          </div>
          <Field label={t("note")}>
            <Textarea className="min-h-20" value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {issuedURL ? (
            <div className="grid gap-2">
              <Label>{t("downloadUrl")}</Label>
              <div className="flex gap-2">
                <Input readOnly value={issuedURL} />
                <Button type="button" variant="outline" onClick={() => void copyText(issuedURL, t("copiedDownloadUrl"))}>
                  <Copy className="h-4 w-4" />
                  {t("copyUrl")}
                </Button>
              </div>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t("common:actions.cancel")}
            </Button>
            <Button
              type="button"
              disabled={mutation.isPending}
              onClick={() => {
                const parsedClientID = Number.parseInt(clientID, 10);
                const parsedMaxDownloads = Number.parseInt(maxDownloads, 10);
                mutation.mutate(
                  {
                    artifactId: artifact.id,
                    clientId: Number.isFinite(parsedClientID) && parsedClientID > 0 ? parsedClientID : undefined,
                    ttlHours: Number.parseInt(ttlHours, 10),
                    maxDownloads: Number.isFinite(parsedMaxDownloads) && parsedMaxDownloads > 0 ? parsedMaxDownloads : 0,
                    note: note.trim()
                  },
                  {
                    onSuccess: (issued) => {
                      setIssuedURL(issued.downloadUrl);
                      toast.success(t("grantCreated"));
                    },
                    onError: showMutationError
                  }
                );
              }}
            >
              <Link2 className="h-4 w-4" />
              {t("createGrant")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArtifactGrantList({ grants, loading, locale }: { grants: ReleaseDownloadGrantRecord[]; loading: boolean; locale: string }) {
  const { t } = useTranslation(["apps", "common"]);
  const revokeMutation = useRevokeReleaseDownloadGrantMutation();

  if (loading) {
    return <div className="text-xs text-muted-foreground">{t("common:states.loading")}</div>;
  }
  if (grants.length === 0) {
    return <div className="text-xs text-muted-foreground">{t("noDownloadGrants")}</div>;
  }

  return (
    <div className="overflow-x-auto rounded border">
      <table className="w-full min-w-[760px] border-collapse text-xs">
        <thead className="bg-muted text-left uppercase text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5">{t("grantStatus")}</th>
            <th className="px-2 py-1.5">{t("clientId")}</th>
            <th className="px-2 py-1.5">{t("expiresAt")}</th>
            <th className="px-2 py-1.5">{t("downloads")}</th>
            <th className="px-2 py-1.5">{t("note")}</th>
            <th className="px-2 py-1.5 text-right">{t("common:actions.open")}</th>
          </tr>
        </thead>
        <tbody>
          {grants.map((grant) => (
            <tr key={grant.id} className="border-t">
              <td className="px-2 py-1.5">
                <DownloadGrantStatusBadge status={grant.effectiveStatus} />
              </td>
              <td className="px-2 py-1.5">{grant.clientId || "—"}</td>
              <td className="px-2 py-1.5 text-muted-foreground">{formatDateTime(grant.expiresAt, locale)}</td>
              <td className="px-2 py-1.5">
                {grant.downloadCount}
                {grant.maxDownloads > 0 ? `/${grant.maxDownloads}` : ""}
              </td>
              <td className="max-w-56 truncate px-2 py-1.5">{grant.note || "—"}</td>
              <td className="px-2 py-1.5 text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={grant.status !== "ACTIVE" || revokeMutation.isPending}
                  onClick={() => {
                    const reason = window.prompt(t("revokeGrantReasonPrompt"), "sent to wrong user");
                    if (!reason?.trim()) {
                      return;
                    }
                    revokeMutation.mutate({ grantId: grant.id, reason }, { onSuccess: () => toast.success(t("grantRevoked")), onError: showMutationError });
                  }}
                >
                  <XCircle className="h-4 w-4" />
                  {t("revokeGrant")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UploadArtifactForm({ releaseId, artifacts }: { releaseId: string; artifacts: ReleaseArtifactRecord[] }) {
  const { t } = useTranslation(["apps"]);
  const mutation = useUploadReleaseArtifactMutation();
  const [osName, setOSName] = useState("linux");
  const [arch, setArch] = useState("amd64");
  const [kind, setKind] = useState<(typeof artifactKinds)[number]>("archive");
  const [defaultDownload, setDefaultDownload] = useState(false);
  const [signingState, setSigningState] = useState<(typeof artifactSigningStates)[number]>("not_applicable");
  const [webView2Strategy, setWebView2Strategy] = useState<(typeof webView2Strategies)[number]>("download");
  const [file, setFile] = useState<File | null>(null);
  const selectedArtifact = artifacts.find((artifact) => artifact.os === osName && artifact.arch === arch && artifact.kind === kind);
  const showWebView2Strategy = kind === "installer" && osName === "windows";

  return (
    <form
      className="grid gap-2 md:grid-cols-[7rem_7rem_8rem_9rem_9rem_minmax(14rem,1fr)_auto] md:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        if (!file) {
          return;
        }
        mutation.mutate(
          {
            releaseId,
            os: osName,
            arch,
            kind,
            defaultDownload,
            signingState,
            webview2Strategy: showWebView2Strategy ? webView2Strategy : undefined,
            file
          },
          {
            onSuccess: () => {
              setFile(null);
              toast.success(t("artifactUploaded"));
            },
            onError: showMutationError
          }
        );
      }}
    >
      <Field label={t("os")}>
        <Select value={osName} onValueChange={setOSName}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {releaseOperatingSystems.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("arch")}>
        <Select value={arch} onValueChange={setArch}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {releaseArchitectures.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("artifactKindLabel")}>
        <Select value={kind} onValueChange={(value) => setKind(value as typeof kind)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {artifactKinds.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`artifactKind.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("signingStateLabel")}>
        <Select value={signingState} onValueChange={(value) => setSigningState(value as typeof signingState)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {artifactSigningStates.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`signingState.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("defaultDownload")}>
        <div className="flex h-10 items-center">
          <Switch checked={defaultDownload} onCheckedChange={setDefaultDownload} />
        </div>
      </Field>
      <Field label={t("artifactFile")}>
        <Input type="file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} required />
      </Field>
      <Button type="submit" disabled={!file || mutation.isPending}>
        <Upload className="h-4 w-4" />
        {selectedArtifact ? t("replaceArtifact") : t("upload")}
      </Button>
      {selectedArtifact ? (
        <div className="md:col-span-7 text-xs text-muted-foreground">
          {t("replaceArtifactTarget", { filename: selectedArtifact.filename })}
        </div>
      ) : null}
      {showWebView2Strategy ? (
        <div className="md:col-span-2">
          <Field label={t("webview2Strategy")}>
            <Select value={webView2Strategy} onValueChange={(value) => setWebView2Strategy(value as typeof webView2Strategy)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {webView2Strategies.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      ) : null}
    </form>
  );
}

function StatusBadge({ status }: { status: ReleaseStatus }) {
  const variant = status === "PUBLISHED" ? "success" : status === "YANKED" ? "destructive" : "warning";
  return <Badge variant={variant}>{status}</Badge>;
}

function DownloadGrantStatusBadge({ status }: { status: ReleaseDownloadGrantRecord["effectiveStatus"] }) {
  const variant = status === "ACTIVE" ? "success" : status === "REVOKED" ? "destructive" : "warning";
  return <Badge variant={variant}>{status}</Badge>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function formatBytes(value: number) {
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KiB`;
  }
  return `${(value / 1024 / 1024).toFixed(1)} MiB`;
}

function showMutationError(error: unknown) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : "Request failed");
}

async function copyText(value: string, message: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Clipboard is unavailable");
  }
}
